import styles from "../styles/HomeSectionTransactionTable.module.scss";
import clsx from "clsx";

import { useState, useEffect } from "react";
import { useIsMobile } from "../providers/IsMobileProvider";
import { HomeTableColumns, HomeTableProps } from "../utils";

import Table from "./common/Table";
import {getTransactionHistory, TransactionProps} from "../utils/creditService";
import {useAuth} from "../providers/AuthProvider";

interface HomeSectionTransactionTableProps {
  filter: string;
  creditLineId?: number;
  onDataChange?: (data: HomeTableProps[]) => void;
}


function HomeSectionTransactionTable({ filter, creditLineId, onDataChange }: HomeSectionTransactionTableProps) {
  const { isMobile } = useIsMobile();
  const columns = HomeTableColumns(isMobile);

  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<HomeTableProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!isAuthenticated) {
        console.log("Not authenticated, skipping fetch");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const transactions = await getTransactionHistory(filter, '-timestamp', 10, creditLineId);

        const formattedData = transactions.map((transaction: TransactionProps) => ({
          method: transaction.channel,
          stablecoin_amount: {
            currency: transaction.denom,
            amount: transaction.amount,
          },
          status: transaction.status,
          transaction_id: transaction.transaction_hash,
          transaction_link: transaction.transaction_link,
          payout_amount: {
            currency: transaction.denom,
            amount: transaction.amount,
          },
          date: transaction.timestamp,
        }));

        setData(formattedData);
        
        // Notify parent component of data change for CSV export
        if (onDataChange) {
          onDataChange(formattedData);
        }
      } catch (err: any) {
        console.error("Transaction fetch error:", {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers,
          filter: filter
        });

        setError(err.response?.data?.detail || "Failed to fetch transactions");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [filter, creditLineId, isAuthenticated, onDataChange]);

  return (
    <section className={clsx(styles.cHomeSectionTransactionTable, "c-home-section-transaction-table")}>
      <Table
        isLoading={loading}
        data={data}
        columns={columns}
        pageSize={5}
        customDefaultEmpty
        defaultEmptyHeading='No transactions'
        defaultEmptyTitle={error? "Failed to fetch transactions" : "You have no transactions at the moment"}
      />
    </section>
  );
}

export default HomeSectionTransactionTable;
